"use client";
import React, { Suspense, useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useSharedData } from "bossjob-remote/dist/hooks";
import { buildQuery } from "helpers/buildQuery";
import { removeCookie } from "helpers/cookies";
import { getCookie } from "helpers/cookies";
import { getDeviceUuid } from "helpers/guest";
import { addJobViewService } from "store/services/jobs/addJobView";
import styles from "./Search.pc.module.scss";
import { Input, Button } from "antd";
import {
  JOB_VIEW_BURIED_COOKIE,
  getDeviceNameTracker,
  viewJobSourceTracker
} from "components/ReportTracker/util";

type pagePropsType = {
  jobId?: number;
  data: any;
};

const Search = ({ jobId, data }: pagePropsType) => {
  const router = useRouter();
  const token = getCookie("sessionid");
  const viewJobTracker = getCookie(JOB_VIEW_BURIED_COOKIE);
  const searchParams = useSearchParams();
  const { location, setLocation } = useSharedData("LOCATION_CONTEXT") ?? {};
  const [searchValue, setSearchValue] = useState<string>("");
  let shared_from = searchParams.get("shared_from");
  shared_from = (shared_from || "").trim();

  useEffect(() => {
    if (jobId) {
      handleFetchAddJobViewService();
      addJobViewFromSharedService();
    }
  }, [jobId]);

  const handleUpdatePath = () => {
    const path = buildQuery(location?.seo_value, searchValue);
    router.push(path);
  };

  // add job view from shared url params
  const addJobViewFromSharedService = async () => {
    try {
      if (!shared_from) return;
      const query = {
        jobId,
        status: "public"
      };

      if (token) {
        query.status = token ? "protected" : "public";
      }
      const { reco_from, query_id } = viewJobTracker || {};
      const deviceUuid = await getDeviceUuid();
      const tokenData = {
        source: shared_from,
        device: getDeviceNameTracker(false),
        reco_from: reco_from ? reco_from : null,
        device_udid: deviceUuid,
        is_login: token ? 1 : 0,
        query_id: query_id || null
      };
      if (!tokenData.reco_from) {
        delete tokenData.reco_from;
      }

      const params = Object.assign(query, tokenData);

      addJobViewService(params).finally(() => {
        removeCookie(JOB_VIEW_BURIED_COOKIE);
      });
    } catch (error) {}
  };

  const handleFetchAddJobViewService = async () => {
    try {
      if (!viewJobTracker) return;
      const query = {
        jobId,
        status: "public"
      };

      if (token) {
        query.status = token ? "protected" : "public";
      }
      let _source = "";
      const { source, reco_from, query_id } = viewJobTracker || {};
      const deviceUuid = await getDeviceUuid();
      const normalSource = searchParams.get("source") || source || viewJobSourceTracker.jobsHiring;
      const hackquest = searchParams.get("apply_url");
      _source = hackquest ? `${hackquest}-${normalSource}` : normalSource;

      const tokenData = {
        source: _source,
        device: getDeviceNameTracker(false),
        reco_from: reco_from ? reco_from : null,
        device_udid: deviceUuid,
        is_login: token ? 1 : 0,
        query_id: query_id || null
      };
      if (!tokenData.reco_from) {
        delete tokenData.reco_from;
      }

      const params = Object.assign(query, tokenData);

      addJobViewService(params).finally(() => {
        removeCookie(JOB_VIEW_BURIED_COOKIE);
      });
    } catch (error) {}
  };
  return (
    <div className={styles.searchWrap}>
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='24'
        height='25'
        viewBox='0 0 24 25'
        fill='none'
      >
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M16.546 21.8436L12.1016 16.8436L13.3472 15.7363L17.7917 20.7363L16.546 21.8436Z'
          fill='black'
        />
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M9.38889 7.12174C6.78088 7.12174 4.66667 9.23596 4.66667 11.844C4.66667 14.452 6.78088 16.5662 9.38889 16.5662C11.9969 16.5662 14.1111 14.452 14.1111 11.844C14.1111 9.23596 11.9969 7.12174 9.38889 7.12174ZM3 11.844C3 8.31548 5.8604 5.45508 9.38889 5.45508C12.9174 5.45508 15.7778 8.31548 15.7778 11.844C15.7778 15.3725 12.9174 18.2329 9.38889 18.2329C5.8604 18.2329 3 15.3725 3 11.844Z'
          fill='black'
        />
        <path
          d='M16 5.31871C18.1774 5.31871 19.4839 3.72781 19.4839 1.45508C19.4839 3.72781 21.0081 5.31871 22.75 5.31871C21.0081 5.31871 19.4839 6.68235 19.4839 8.95508C19.4839 6.68235 18.1774 5.31871 16 5.31871Z'
          fill='black'
        />
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M18.2767 3.99172C18.8026 3.39284 19.1089 2.52175 19.1089 1.45508H19.8589C19.8589 3.56793 21.2594 4.94371 22.75 4.94371V5.69371C21.2301 5.69371 19.8589 6.87416 19.8589 8.95508H19.1089C19.1089 7.88948 18.8036 7.08254 18.2877 6.54405C17.7737 6.0076 17.0088 5.69371 16 5.69371V4.94371C16.9911 4.94371 17.7556 4.58506 18.2767 3.99172ZM17.7891 5.29657C18.1849 5.47396 18.5344 5.71743 18.8292 6.0252C19.1103 6.31857 19.3347 6.6629 19.5013 7.05091C19.8682 6.24913 20.4674 5.64831 21.1904 5.2975C20.4641 4.90593 19.8673 4.24449 19.5018 3.40531C19.3371 3.8087 19.116 4.17261 18.8402 4.48662C18.5447 4.82312 18.1918 5.09597 17.7891 5.29657Z'
          fill='black'
        />
      </svg>
      <Suspense>
        <Input
          value={searchValue}
          placeholder={data.input_text}
          onChange={(e) => setSearchValue(e.target?.value)}
          onKeyUp={(e) => e.code == "Enter" && handleUpdatePath()}
          maxLength={60}
        />
        <Button type='primary' onClick={handleUpdatePath}>
          {data.search_text}
        </Button>
      </Suspense>
    </div>
  );
};

export default Search;
